/**
 * Browser-style tabs for switching between audit sheets.
 *
 * One tab per inventory sheet (Discrepancies / Pending / Completed /
 * Unclassified / Dealer Duplicates) plus — when the run has a chained
 * website run — the three website sheets (Matched / Missing from Site /
 * Should Be Removed), folded in from the retired standalone page. Active
 * tab uses Excel-matched color. Inventory clicks update AuditContext;
 * website clicks are the page's own state (a different data source).
 */
import React from "react"
import { useAudit, KNOWN_SHEETS, SheetSlug } from "../../context/AuditContext"
import { AuditRunSummary } from "../../api/audit"
import { THEME } from "../../config/theme"

export const WEBSITE_TAB_SLUGS = ["matched", "missing_from_site", "should_be_removed"] as const
export type WebsiteTabSlug = (typeof WEBSITE_TAB_SLUGS)[number]

interface Props {
  run: AuditRunSummary | null
  /** The chained website run — null hides the website tabs entirely. */
  websiteRun?: AuditRunSummary | null
  /** Non-null when a website tab is active (page-level state). */
  activeWebsiteSheet?: WebsiteTabSlug | null
  onWebsiteSheetSelect?: (sheet: WebsiteTabSlug) => void
  onSheetChange?: (sheet: string) => void
}

const SHEET_LABELS: Record<string, string> = {
  discrepancies: "Discrepancies",
  pending: "Pending",
  completed: "Completed",
  unclassified: "Unclassified",
  dealer_duplicates: "Dealer Duplicates",
}

function countForSheet(run: AuditRunSummary | null, slug: string): number | null {
  if (!run) return null
  switch (slug) {
    case "discrepancies": return run.discrepancies_count
    case "pending": return run.pending_count
    case "completed": return run.completed_count
    case "unclassified": return run.unclassified_count
    case "dealer_duplicates": return run.dealer_duplicates_count
    default: return null
  }
}

const WEBSITE_TAB_LABELS: Record<WebsiteTabSlug, string> = {
  matched: "Matched",
  missing_from_site: "Missing from Site",
  should_be_removed: "Should Be Removed",
}

function websiteCountFor(run: AuditRunSummary, slug: WebsiteTabSlug): number | null {
  switch (slug) {
    case "matched": return run.matched_count
    case "missing_from_site": return run.missing_count
    case "should_be_removed": return run.should_remove_count
  }
}

export default function AuditTabs({
  run, websiteRun = null, activeWebsiteSheet = null, onWebsiteSheetSelect, onSheetChange,
}: Props) {
  const { currentSheet, setCurrentSheet } = useAudit()

  const handleClick = (slug: SheetSlug) => {
    setCurrentSheet(slug)
    onSheetChange?.(slug)
  }

  const tabStyle = (slug: string): React.CSSProperties => {
    // A website tab being active means NO inventory tab is.
    const isActive =
      activeWebsiteSheet !== null ? slug === activeWebsiteSheet : slug === currentSheet
    const tabColor =
      THEME.auditTable.tabColors[slug as keyof typeof THEME.auditTable.tabColors] ??
      "#EEEEEE"
    return {
      padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
      cursor: "pointer",
      backgroundColor: isActive ? tabColor : "transparent",
      borderBottom: isActive
        ? `3px solid ${THEME.auditTable.activeTabBorderBottom}`
        : "3px solid transparent",
      color: isActive ? "#000" : THEME.auditTable.inactiveTabColor,
      fontWeight: isActive ? 600 : 400,
      fontSize: 14,
      userSelect: "none",
      transition: "background-color 0.15s, border-color 0.15s",
    }
  }

  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
        marginBottom: THEME.spacing.md,
      }}
    >
      {KNOWN_SHEETS.map((slug) => {
        const count = countForSheet(run, slug)
        return (
          <div
            key={slug}
            style={tabStyle(slug)}
            onClick={() => handleClick(slug)}
          >
            {SHEET_LABELS[slug] ?? slug}
            {count !== null && (
              <span
                style={{
                  marginLeft: 6,
                  color: THEME.colors.neutral.light.textSecondary,
                  fontWeight: 400,
                }}
              >
                ({count.toLocaleString()})
              </span>
            )}
          </div>
        )
      })}
      {websiteRun && (
        <div
          aria-hidden
          style={{
            width: 1,
            margin: `6px ${THEME.spacing.sm}`,
            background: THEME.auditTable.borderColor,
          }}
        />
      )}
      {websiteRun &&
        WEBSITE_TAB_SLUGS.map((slug) => {
          const count = websiteCountFor(websiteRun, slug)
          return (
            <div
              key={slug}
              style={tabStyle(slug)}
              onClick={() => onWebsiteSheetSelect?.(slug)}
            >
              {WEBSITE_TAB_LABELS[slug]}
              {count !== null && (
                <span
                  style={{
                    marginLeft: 6,
                    color: THEME.colors.neutral.light.textSecondary,
                    fontWeight: 400,
                  }}
                >
                  ({count.toLocaleString()})
                </span>
              )}
            </div>
          )
        })}
    </div>
  )
}
